import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabaseClient';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import SolDivider from '../components/ui/SolDivider';
import {
  mapNegocioFromDb,
  negocioTipoToUi,
  negocioFotoPublicUrl,
  contactoHref,
} from '../lib/negocioHelpers';

export default function PublicNegocio() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, currentUser } = useApp();
  const [listing, setListing] = useState(null);
  const [memberCredit, setMemberCredit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setNotFound(false);
      try {
        const { data, error } = await supabase.rpc('get_negocio_page_by_slug', {
          p_slug: slug,
        });
        if (error) throw error;

        const row = Array.isArray(data) ? data[0] : data;
        if (!row) {
          if (!cancelled) {
            setListing(null);
            setMemberCredit(null);
            setNotFound(true);
          }
          return;
        }

        if (!cancelled) {
          setListing(mapNegocioFromDb(row));
          setMemberCredit({
            id: row.member_id,
            nombre: row.member_nombre,
            foto_url: row.member_foto_url,
          });
        }
      } catch (err) {
        console.error('No se pudo cargar el negocio:', err);
        if (!cancelled) {
          setNotFound(true);
          setListing(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-4 border-[#74ACDF] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (notFound || !listing) {
    return (
      <div className="max-w-xl mx-auto py-20 text-center space-y-4">
        <h1 className="font-editorial text-3xl text-[#1B2A4E]">No encontrado</h1>
        <p className="text-stone-500">Este negocio o servicio no existe o no está disponible.</p>
        <button
          type="button"
          onClick={() => navigate(isAuthenticated ? '/' : '/')}
          className="text-[#74ACDF] font-bold hover:text-[#1B2A4E]"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  const logoSrc = negocioFotoPublicUrl(listing.logo_url);
  const fotos = (listing.fotos || []).map(negocioFotoPublicUrl).filter(Boolean);
  const contactUrl = contactoHref(listing.contacto_tipo, listing.contacto_valor);
  const isOwner = currentUser?.id === listing.member_id;
  const creditLabel = listing.tipo === 'servicio' ? 'Un servicio de' : 'Un negocio de';
  const showMemberLink = isAuthenticated && memberCredit?.id;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`max-w-4xl mx-auto space-y-8 ${!isAuthenticated ? 'px-4 py-8' : ''}`}
    >
      {isAuthenticated && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="group flex items-center gap-2 text-stone-400 hover:text-[#1B2A4E] transition-colors font-medium"
        >
          <SafeIcon name="ArrowLeft" className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Volver
        </button>
      )}

      {listing.status !== 'approved' && isOwner && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-5 py-3 rounded-2xl text-sm font-medium">
          Vista previa — este listing está {listing.status === 'pending' ? 'pendiente de aprobación' : listing.status}.
        </div>
      )}

      <div className="bg-white rounded-[40px] shadow-2xl shadow-stone-200/50 border border-stone-100 overflow-hidden">
        <div className="h-48 bg-[#1B2A4E] relative">
          <div className="absolute inset-0 opacity-10 sol-watermark" />
          <div className="absolute -bottom-16 left-8 md:left-12">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={listing.nombre}
                className="w-36 h-36 md:w-40 md:h-40 rounded-[32px] object-cover border-8 border-white shadow-xl bg-white"
              />
            ) : (
              <div className="w-36 h-36 md:w-40 md:h-40 rounded-[32px] border-8 border-white shadow-xl bg-stone-100 flex items-center justify-center text-[#1B2A4E]">
                <SafeIcon name="Briefcase" className="w-12 h-12" />
              </div>
            )}
          </div>
        </div>

        <div className="pt-20 px-8 md:px-12 pb-12">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-editorial font-bold text-[#1B2A4E]">
                {listing.nombre}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="bg-[#1B2A4E] text-white px-4 py-1.5 rounded-full label-technical text-xs">
                  {negocioTipoToUi(listing.tipo)}
                </span>
                {listing.categoria && (
                  <span className="bg-[#74ACDF]/10 text-[#74ACDF] px-4 py-1.5 rounded-full label-technical text-xs">
                    {listing.categoria}
                  </span>
                )}
                {listing.ubicacion && (
                  <div className="flex items-center gap-1.5 text-stone-400 font-medium">
                    <SafeIcon name="MapPin" className="w-4 h-4 text-[#F6B40E]" />
                    {listing.ubicacion}
                  </div>
                )}
              </div>
            </div>

            {contactUrl && (
              <a
                href={contactUrl}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-bold transition-all shadow-lg hover:scale-105 ${
                  listing.contacto_tipo === 'whatsapp'
                    ? 'bg-[#25D366] hover:bg-[#20bd5a] text-white'
                    : 'bg-[#1B2A4E] hover:bg-[#253969] text-white'
                }`}
              >
                <SafeIcon
                  name={listing.contacto_tipo === 'whatsapp' ? 'MessageCircle' : 'ExternalLink'}
                  className="w-5 h-5"
                />
                {listing.contacto_tipo === 'whatsapp' ? 'Contactar por WhatsApp' : 'Visitar link'}
              </a>
            )}
          </div>

          <SolDivider className="my-10" />

          {listing.descripcion && (
            <div className="mb-10">
              <h3 className="text-xl font-editorial font-bold text-[#1B2A4E] mb-4">Descripción</h3>
              <p className="text-lg text-[#1B2A4E] leading-relaxed whitespace-pre-wrap">
                {listing.descripcion}
              </p>
            </div>
          )}

          {fotos.length > 0 && (
            <div className="mb-10">
              <h3 className="text-xl font-editorial font-bold text-[#1B2A4E] mb-4">Galería</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {fotos.map((src) => (
                  <img
                    key={src}
                    src={src}
                    alt=""
                    className="w-full aspect-square object-cover rounded-2xl border border-stone-100"
                  />
                ))}
              </div>
            </div>
          )}

          {listing.tipo === 'negocio' && listing.items?.length > 0 && (
            <div className="mb-10">
              <h3 className="text-xl font-editorial font-bold text-[#1B2A4E] mb-4">
                Productos y servicios
              </h3>
              <div className="space-y-4">
                {listing.items.map((item, idx) => (
                  <div
                    key={`${item.nombre}-${idx}`}
                    className="flex gap-4 p-5 rounded-3xl bg-stone-50 border border-stone-100"
                  >
                    {item.foto_url && (
                      <img
                        src={negocioFotoPublicUrl(item.foto_url)}
                        alt=""
                        className="w-20 h-20 rounded-2xl object-cover shrink-0"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h4 className="font-bold text-[#1B2A4E]">{item.nombre}</h4>
                        {item.precio && (
                          <span className="label-technical text-[#74ACDF]">{item.precio}</span>
                        )}
                      </div>
                      {item.descripcion && (
                        <p className="text-sm text-stone-600 mt-1 leading-relaxed">{item.descripcion}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {memberCredit?.nombre && (
            <div className="pt-8 border-t border-stone-100 flex items-center gap-4">
              {memberCredit.foto_url && (
                <img
                  src={memberCredit.foto_url}
                  alt=""
                  className="w-12 h-12 rounded-xl object-cover border border-stone-200"
                />
              )}
              <p className="text-stone-500">
                {creditLabel}{' '}
                {showMemberLink ? (
                  <Link
                    to={`/miembro/${memberCredit.id}`}
                    className="font-bold text-[#1B2A4E] hover:text-[#74ACDF] transition-colors"
                  >
                    {memberCredit.nombre}
                  </Link>
                ) : (
                  <span className="font-bold text-[#1B2A4E]">{memberCredit.nombre}</span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
